import { useState, useCallback } from 'react';
import { aiCorrecting } from '../services/api';
import { getUserData } from '../utils/userData';

// 類型定義
interface CorrectionOptions {
  bookId: string;
  chapterId: string;
  classCode: string;
  className: string;
  studentInput?: string;
  files?: File | File[];
}

interface CorrectionResult {
  _id: string;
  ticket?: string;
  result?: string;
  status?: string;
  createdAt?: string;
  modifiedAt?: string;
}

interface UseCorrectionReturn {
  isSubmitting: boolean;
  submitCorrection: (options: CorrectionOptions) => Promise<CorrectionResult>;
  error: string | null;
  clearError: () => void;
}

export const useCorrection = (): UseCorrectionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitCorrection = useCallback(async (options: CorrectionOptions): Promise<CorrectionResult> => {
    const { bookId, chapterId, classCode, className, studentInput, files } = options;
    
    setIsSubmitting(true);
    setError(null);

    try {
      const userData = getUserData();
      if (!userData?.sub) {
        throw new Error('用戶認證失敗');
      }

      const response = await aiCorrecting(
        userData.sub,
        bookId,
        chapterId,
        userData.name || '學生用戶',
        userData.email || '',
        classCode,
        className,
        studentInput,
        files
      );

      if (response.data) {
        return response.data;
      } else {
        throw new Error('API 回應格式錯誤');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = error.response?.data?.detail || error.message || '批改提交失敗';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isSubmitting,
    submitCorrection,
    error,
    clearError
  };
};